import { uid } from '@runtime/shared/utils/id'
import {
  computeLevelFromRating,
  saveFullGame,
  type FullGameRecord,
} from '@runtime/core/user/userModel'
import type {
  AppStoreState,
  GameSummary,
} from '@runtime/core/store/appStoreTypes'
import type { AppStoreSlice } from '@runtime/core/store/slices/sliceTypes'
import {
  applyGameResult,
  pendingResultFromHistory,
} from '@runtime/core/user/gameResults'
import { pickInitialGameResultsOutbox } from '@runtime/core/store/appStorePersist'

type GamesSlice = Pick<
  AppStoreState,
  | 'recordGame'
  | 'toggleFavoriteGame'
  | 'gameResultsVersion'
  | 'gameResultsOutbox'
  | 'acknowledgeGameResults'
>

function toFullGameRecord(summary: GameSummary): FullGameRecord {
  const id = summary.id != null ? String(summary.id) : uid()
  return {
    ...summary,
    id,
    timestamp:
      typeof summary.timestamp === 'number' ? summary.timestamp : Date.now(),
  }
}

export const createGamesSlice: AppStoreSlice<GamesSlice> = (set) => ({
  gameResultsOutbox: pickInitialGameResultsOutbox(),
  gameResultsVersion: 1,
  acknowledgeGameResults: (userId, ids, snapshot) =>
    set((s) => {
      const acknowledged = new Set(ids)
      const remaining = (s.gameResultsOutbox[userId] ?? []).filter(
        (result) => !acknowledged.has(result.id),
      )
      const gameResultsOutbox = { ...s.gameResultsOutbox, [userId]: remaining }
      if (s.user?.id !== userId) return { gameResultsOutbox }
      const projected = remaining.reduce(applyGameResult, snapshot)
      return {
        gameResultsOutbox,
        user: {
          ...s.user,
          ...projected,
          level: computeLevelFromRating(projected.rating),
        },
      }
    }),
  recordGame: (summary?: GameSummary | null) => {
    if (!summary) return
    summary = {
      ...summary,
      id: summary.id ?? uid(),
      timestamp: summary.timestamp ?? Date.now(),
    }

    // Save detailed game when available (final board / moves)
    if (summary.finalBoard || summary.moves) {
      try {
        saveFullGame(toFullGameRecord(summary))
      } catch {
        // ignore
      }
    }

    set((s) => {
      const prev = s.user
      if (!prev) return {}
      const gameId = summary.id != null ? String(summary.id) : uid()
      if (
        prev.history?.some((entry) => entry.id === gameId) ||
        s.gameResultsOutbox[prev.id]?.some((entry) => entry.id === gameId)
      )
        return {}

      let meIndex = summary.meIndex
      const players = Array.isArray(summary.players) ? summary.players : []

      if (meIndex == null) {
        const pseudo = String(prev.pseudo || '').toLowerCase()
        if (pseudo) {
          meIndex = players.findIndex(
            (p) => String(p?.name || '').toLowerCase() === pseudo,
          )
        }
      }
      if (meIndex == null || meIndex < 0 || !players[meIndex]) return {}

      const me = players[meIndex]
      const winningPlayerIndexes = Array.isArray(summary.winningPlayerIndexes)
        ? summary.winningPlayerIndexes
        : null
      const isDraw =
        typeof summary.isDraw === 'boolean'
          ? summary.isDraw
          : winningPlayerIndexes
            ? winningPlayerIndexes.length === 0
            : summary.winnerIndex == null
      const isWin =
        !isDraw &&
        (winningPlayerIndexes
          ? winningPlayerIndexes.includes(meIndex)
          : summary.winnerIndex === meIndex)
      const isLoss = !isDraw && !isWin
      const resultGroups = Array.isArray(summary.resultGroups)
        ? summary.resultGroups
        : []
      const myResultGroup = resultGroups.find((group) =>
        group.memberPlayerIndexes?.includes(meIndex),
      )
      const ownGroupPlayerIndexes = new Set(
        myResultGroup?.memberPlayerIndexes ?? [meIndex],
      )

      const history = Array.isArray(prev.history) ? [...prev.history] : []
      const oppNames = players
        .filter((_, playerIndex) => !ownGroupPlayerIndexes.has(playerIndex))
        .map((player) => player.name)
        .filter(
          (name): name is string => typeof name === 'string' && name.length > 0,
        )
      const scoreFor =
        myResultGroup?.score ?? (me.score != null ? me.score : null)
      let scoreAgainst = null
      if (myResultGroup && resultGroups.length === 2) {
        scoreAgainst =
          resultGroups.find((group) => group !== myResultGroup)?.score ?? null
      } else if (players.length === 2 && players[1 - meIndex]) {
        scoreAgainst = players[1 - meIndex].score
      }

      history.unshift({
        id: gameId,
        ts: summary.timestamp || Date.now(),
        kind: summary.kind,
        grid: summary.grid,
        vsAI: !!summary.vsAI,
        myName: me.name,
        opponents: oppNames,
        result: isWin ? 'win' : isLoss ? 'loss' : 'draw',
        scoreFor,
        scoreAgainst,
        durationMs: myResultGroup?.timeMs ?? me.timeMs,
      })
      if (history.length > 50) history.length = 50

      const pending = pendingResultFromHistory(history[0])!
      const { games, rating } = applyGameResult(
        { games: prev.games ?? {}, rating: prev.rating ?? 1200 },
        pending,
      )
      const level = computeLevelFromRating(rating)
      const favorites = prev.favorites || []

      const nextUser = {
        ...prev,
        games,
        rating,
        level,
        history,
        favorites,
      }

      return {
        user: nextUser,
        gameResultsOutbox: {
          ...s.gameResultsOutbox,
          [prev.id]: [...(s.gameResultsOutbox[prev.id] ?? []), pending],
        },
      }
    })
  },

  toggleFavoriteGame: (gameId: string) => {
    if (!gameId) return
    set((s) => {
      const prev = s.user
      if (!prev) return {}
      const favorites = Array.isArray(prev.favorites) ? [...prev.favorites] : []
      const idx = favorites.indexOf(gameId)
      if (idx === -1) favorites.push(gameId)
      else favorites.splice(idx, 1)

      const nextUser = { ...prev, favorites }
      return { user: nextUser }
    })
  },
})
